import React, { useEffect, useRef } from "react";
import AppShell from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";
import AssistantAnswer from "@/components/assistant/AssistantAnswer";
import { useAssistantAnswer } from "@/lib/assistant";

export default function AssistantPage() {
  const {
    workspaceState, sessionId, history, isSending,
    inputDraft, setInputDraft, inputError, persistentAlert, send,
  } = useAssistantAnswer();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (typeof scrollRef.current?.scrollTo === "function") {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [history, isSending]);

  const disabled = workspaceState !== "ready" || isSending;

  return (
    <AppShell subtitle="◆ KNOWLEDGE ASSISTANT · GROUNDED RAG" title="Workspace knowledge assistant">
      <div className="max-w-3xl mx-auto" data-testid="assistant-page-root">
        {workspaceState === "loading" && (
          <div data-testid="assistant-workspace-loading" className="space-y-2">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}
        {workspaceState === "unavailable" && (
          <Alert
            data-testid="assistant-workspace-unavailable"
            variant="destructive"
            className="mb-4"
          >
            <AlertTitle>Workspace unavailable</AlertTitle>
            <AlertDescription>
              We couldn't determine your workspace. Please refresh the page or contact support.
            </AlertDescription>
          </Alert>
        )}
        {persistentAlert && (
          <Alert
            data-testid="assistant-persistent-alert"
            variant={persistentAlert.variant === "destructive" ? "destructive" : "default"}
            className="mb-4"
          >
            <AlertTitle>{persistentAlert.title}</AlertTitle>
            <AlertDescription>{persistentAlert.body}</AlertDescription>
          </Alert>
        )}
        {workspaceState === "ready" && (
          <>
            <div
              ref={scrollRef}
              className="border border-black/10 rounded-md p-6 overflow-y-auto space-y-6 h-[calc(100vh-320px)] min-h-[380px]"
              data-testid="assistant-messages"
            >
              {history.length === 0 && !isSending && (
                <div className="text-sm text-neutral-500 flex items-center gap-2">
                  <Sparkle size={14} weight="fill" className="text-[#0B4DCC]" />
                  Ask a question about the knowledge base attached to this workspace.
                </div>
              )}
              {history.map((turn) => (
                <AssistantAnswer key={String(turn.turnId)} turn={turn} />
              ))}
              {isSending && (
                <div className="flex justify-start">
                  <div className="bg-[#F4F6F8] text-neutral-500 px-5 py-3 rounded-md text-sm border border-black/5">
                    <div className="flex items-center gap-2">
                      <Sparkle size={12} weight="fill" className="text-[#0B4DCC] animate-pulse" />
                      Searching workspace knowledge…
                    </div>
                  </div>
                </div>
              )}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="mt-4 space-y-1"
              data-testid="assistant-form"
            >
              <div className="flex gap-2">
                <Input
                  value={inputDraft}
                  onChange={(e) => setInputDraft(e.target.value)}
                  placeholder="Ask a question grounded in your workspace…"
                  className="h-12 rounded-md flex-1"
                  data-testid="assistant-input"
                  disabled={disabled}
                />
                <Button
                  type="submit"
                  disabled={disabled || !inputDraft.trim()}
                  className="fx-btn-primary h-12 px-5 rounded-md"
                  data-testid="assistant-submit"
                >
                  <PaperPlaneTilt size={16} weight="bold" />
                </Button>
              </div>
              {inputError && (
                <div className="text-xs text-red-600" data-testid="assistant-input-error">
                  {inputError}
                </div>
              )}
              {sessionId && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-400">
                  session active
                </div>
              )}
            </form>
          </>
        )}
      </div>
    </AppShell>
  );
}
